import { api } from "@/features/auth/api";

export type AddressLabel = "home" | "work" | "other";

export type Address = {
    id: string;
    label: AddressLabel;
    full_name: string;
    phone: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    pincode: string;
    lat?: number | null;
    lng?: number | null;
    landmark: string;
    is_default: boolean;
};

export type AddressUpsert = Omit<Address, "id">;

export const addressApi = {
    list: async (): Promise<Address[]> =>
        (await api.get<Address[]>("/addresses")).data,
    create: async (payload: AddressUpsert): Promise<Address> =>
        (await api.post<Address>("/addresses", payload)).data,
    update: async (id: string, payload: AddressUpsert): Promise<Address> =>
        (await api.patch<Address>(`/addresses/${id}`, payload)).data,
    remove: async (id: string): Promise<void> => {
        await api.delete(`/addresses/${id}`);
    },
    setDefault: async (id: string): Promise<Address[]> =>
        (await api.post<Address[]>(`/addresses/${id}/default`)).data,
};

// ---------------------------------------------------------------------------
// Orders + coupons + quote
// ---------------------------------------------------------------------------

export type Coupon = {
    code: string;
    title: string;
    description: string;
    kind: "flat" | "percent" | "free_delivery";
    value: number;
    min_order: number;
    max_discount: number;
};

export type PriceBreakdown = {
    subtotal: number;
    mrp_total: number;
    delivery_charge: number;
    platform_fee: number;
    handling_fee: number;
    gst: number;
    coupon_code?: string | null;
    coupon_discount: number;
    total: number;
    total_savings: number;
};

export type OrderStatus =
    | "pending_payment"
    | "placed"
    | "confirmed"
    | "packing"
    | "out_for_delivery"
    | "delivered"
    | "cancelled";

export type OrderItem = {
    product_id: string;
    slug: string;
    name: string;
    brand: string;
    weight: string;
    tone: string[];
    glyph: string;
    unit_price: number;
    unit_mrp: number;
    quantity: number;
    line_total: number;
};

export type Order = {
    id: string;
    number: string;
    status: OrderStatus;
    items: OrderItem[];
    breakdown: PriceBreakdown;
    address: Record<string, unknown> & { full_name: string; line1: string; line2: string; city: string; state: string; pincode: string; phone: string; label: string };
    payment: {
        method: string;
        status: string;
        txn_id?: string | null;
        error?: string | null;
        gateway?: string | null;
        gateway_order_id?: string | null;
        key_id?: string | null;
        amount_paise?: number | null;
        currency?: string | null;
    };
    delivery_slot: string;
    slot_time?: string | null;
    estimated_delivery_at: string;
    placed_at: string;
    cancelled_at?: string | null;
    timeline: Array<{ status: OrderStatus; at: string; completed: boolean }>;
};

export type PlaceOrderPayload = {
    address_id: string;
    payment_method: string;
    delivery_slot: string;
    coupon_code?: string | null;
    slot_time?: string | null;
};

export const orderApi = {
    listCoupons: async (): Promise<Coupon[]> =>
        (await api.get<Coupon[]>("/coupons")).data,
    validateCoupon: async (code: string): Promise<{
        valid: boolean;
        error?: string | null;
        coupon?: Coupon | null;
        breakdown?: PriceBreakdown | null;
    }> => (await api.post("/coupons/validate", { code })).data,
    quote: async (coupon_code?: string | null): Promise<PriceBreakdown> =>
        (await api.post<PriceBreakdown>("/orders/quote", { coupon_code })).data,
    place: async (payload: PlaceOrderPayload): Promise<Order> =>
        (await api.post<Order>("/orders", payload)).data,
    list: async (): Promise<Order[]> =>
        (await api.get<Order[]>("/orders")).data,
    get: async (id: string): Promise<Order> =>
        (await api.get<Order>(`/orders/${id}`)).data,
    cancel: async (id: string): Promise<Order> =>
        (await api.post<Order>(`/orders/${id}/cancel`)).data,
    retryPayment: async (id: string, payment_method?: string): Promise<Order> =>
        (
            await api.post<Order>(
                `/orders/${id}/retry-payment`,
                payment_method ? { payment_method } : {},
            )
        ).data,
    reorder: async (id: string): Promise<{ added: number; message: string }> =>
        (await api.post(`/orders/${id}/reorder`)).data,
};
